/**
 * ONE predicate — "is there a prescription worth SHOWING?".
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * "Will the backend PRINT it?" used to live here too, as
 * `isPrescriptionPrintable` — a hand-transcribed copy of the backend's print
 * guard. That copy was wrong three times in opposite directions (too wide at
 * bk-1168, too narrow at D4), each time fixed by re-transcribing it. The copy
 * WAS the defect, so owner D28 had the backend answer the question itself:
 *
 *   `MetadataEntryResponse.printable` — PER-ENTRY, content-only, computed by
 *   `app.core.prescription_print.has_printable_content`, the SAME function
 *   the `/pdf` and `/preview` 400-guards call. It reaches the client on
 *   `GET /visits/{id}/metadata`, on the Rx save response, and on the patient
 *   card's `prescriptions[]` (`PrescriptionRecordItem` inherits it).
 *
 * `Optional[bool]`: `null` on every non-PRESCRIPTION meta_type and ABSENT on
 * legacy rows. Every ENTRY-GATED Generate-PDF affordance gates on
 * `entry.printable === true` — NEVER on `!== false`, which would turn every
 * note, vital and legacy row into a printable one.
 *
 * "Entry-gated" is doing real work there — THREE of the four PDF affordances
 * are, and the fourth is not:
 *   - `OpdTimelineRow`, `VisitActionsPanel` (list button + read-only card)
 *     → gated on `entry.printable === true`. Correct.
 *   - `ManageVisitPage.jsx` (the per-row "📄 Generate Prescription")
 *     → gated on the PERMISSION ONLY, with no entry check at all, so it is
 *     offered on visits with no prescription and 400s. Pre-existing, and it
 *     fails gracefully (the 400 surfaces inline). Left alone deliberately:
 *     the visit LIST carries no per-row metadata, so gating it client-side
 *     means an N+1 across the busiest screen in the app. The real fix is a
 *     per-VISIT flag on the visit projection, which needs a migration and is
 *     parked. → bk-1179
 *
 * `utils/__tests__/prescriptionContent.test.js` pins the predicate below.
 */

/**
 * hasPrescriptionContent — the ONE front-end answer to "does this digital
 * prescription entry carry anything worth showing as a prescription?".
 *
 * EXACTLY four terms:
 *
 *   medicines · investigations · advised_surgeries · non-blank diagnosis
 *
 * Chief complaint, doctor notes, vitals, symptoms and images are
 * DELIBERATELY NOT content: a C/O-only or notes-only entry carries nothing a
 * prescription card could render, so showing one would be a false presence.
 *
 * NOT a copy of any backend rule, and must not become one. It is WIDER than
 * the backend's print rule by its fourth term, `diagnosis`: a diagnosis-only
 * Rx is worth SHOWING but does not on its own produce a document. That is
 * exactly why the PDF button hangs off the server's `printable` flag instead
 * of this — the two questions differ, and only one of them is the backend's
 * to answer. A legacy row CAN be diagnosis-only with `medicines: null`; the
 * write gate never stopped those from existing, it only stops new ones.
 *
 * Why it exists (bk-1148, bk-1150): every consumer used to require
 * `medicines.length > 0`. A medicine-less prescription (labs only, and after
 * RX-MIN-FIELDS diagnosis-only) is a normal thing a doctor creates — the old
 * rule made the patient card assert "No prescription recorded for this visit."
 * and made patient history render a phantom "📄 File · 👁 View" row with no
 * file behind it.
 *
 * Scope note: this answers CONTENT only. "Is it digital", "is it superseded"
 * and "which entry is the latest" stay with the caller — the selection rules
 * differ per surface (the patient card picks ONE latest active entry; patient
 * history lists every entry).
 *
 * Callers must survive the legacy shape: `medicines` / `investigations` /
 * `advised_surgeries` are `Optional[List[...]] = None` and reach the client as
 * JSON `null`, NOT `[]`. A destructuring default (`const { medicines = [] }`)
 * fires only for `undefined` and will NOT catch that — coerce with `|| []`
 * (bk-1182).
 *
 * @param {object|null|undefined} entry - a PRESCRIPTION `MetadataEntryResponse`.
 * @returns {boolean}
 */
export function hasPrescriptionContent(entry) {
  if (!entry) return false;
  return (
    entry.medicines?.length > 0
    || entry.investigations?.length > 0
    || entry.advised_surgeries?.length > 0
    || (entry.diagnosis || '').trim().length > 0
  );
}
